import React, { Component } from 'react';
import './HubBundles.scss';
import {
  BundleJson,
  BundlesPageJson,
  BundleStatus,
  CourierJson,
  InstructionType,
  TravelMode,
} from '../../types/bundleTypes';
import { fetchCourierByIdList } from '../../fetch/fetchUsers';
import Fetch from '../../utils/fetch';
import { settings } from '../../settings';
import { unique } from '../../utils/array';
import { ReactComponent as BikeIcon } from '../../assets/newsvg/bike.svg';
import { ReactComponent as CarIcon } from '../../assets/svg/car.svg';
import { classNames } from '../../utils/classUtils';
import dayjs, { Dayjs } from 'dayjs';
import { Loader } from '../../components/Loader/Loader';
import { plainToClass } from 'class-transformer';
import { enterFullscreen, leaveFullscreen } from '../../utils/fullscreen';
import { Region } from '../../types/orderTypes';
import { Helmet } from 'react-helmet';

interface State {
  loading: boolean;
  readyBundles: BundleJson[];
  nextBundles: BundleJson[];
  couriers: { [courierId: string]: CourierJson };
  randomTileFlip: number;
  time: Dayjs;
  bundleReadyTimes: { [bundleId: string]: Dayjs };
}

interface Props {
  region: Region;
}

function isBundlePickedUp(bundle: BundleJson): boolean {
  return bundle.instructions
    .filter(instr => instr.type === InstructionType.Pickup)
    .every(instr => instr.completed);
}

export default class HubBundles extends Component<Props, State> {
  private refreshTimerId: NodeJS.Timeout;
  private randomFlipTimerId: NodeJS.Timeout;
  private timeTicker: NodeJS.Timeout;
  private navBarShown: boolean;

  constructor(props) {
    super(props);

    this.state = {
      loading: true,
      readyBundles: [],
      nextBundles: [],
      couriers: {},
      randomTileFlip: -1,
      time: dayjs(),
      bundleReadyTimes: {},
    };
    this.navBarShown = true;
  }

  async componentDidMount() {
    enterFullscreen();
    this.load();

    this.randomFlipTimerId = setInterval(
      () => this.flipRandomTile(),
      60000
    ) as any;
    this.timeTicker = setInterval(() => this.clockTick(), 1000) as any;
  }

  componentWillUnmount() {
    leaveFullscreen();
    clearTimeout(this.refreshTimerId);
    clearInterval(this.randomFlipTimerId);
    clearInterval(this.timeTicker);
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>) {
    if (this.props.region && prevProps.region !== this.props.region) {
      this.load();
    }
  }

  flipRandomTile() {
    if (this.state.readyBundles.length < 2) {
      this.setState({
        randomTileFlip: -1,
      });
    }
    const randomTileFlip = Math.floor(
      Math.random() * (this.state.readyBundles.length + 1)
    );
    this.setState({
      randomTileFlip: randomTileFlip,
    });
  }

  clockTick() {
    this.setState({
      time: dayjs(),
    });
  }

  sortBundles(bundles: BundleJson[]) {
    const bundlesPickedUp = bundles.reduce(function (map, bundle) {
      map[bundle.id] = isBundlePickedUp(bundle);
      return map;
    }, {});
    bundles.sort((b1, b2) => {
      const b1PickedUp = bundlesPickedUp[b1.id];
      const b2PickedUp = bundlesPickedUp[b2.id];
      if (b1PickedUp && !b2PickedUp) {
        return 1;
      } else if (!b1PickedUp && b2PickedUp) {
        return -1;
      }
      const dep1 = departureTime(b1);
      const dep2 = departureTime(b2);
      if (dep1 && !dep2) {
        return -1;
      } else if (!dep1 && dep2) {
        return 1;
      } else if (!dep1 && !dep2) {
        return 0;
      }
      return dep1.diff(dep2, 'second');
    });
  }

  getReadyBundles(bundles: BundleJson[]): BundleJson[] {
    // ready bundles: departure time < 1 hour away (max 5 bundles), or already picked up
    const now = dayjs();
    let readyListItems = 5;
    return bundles.filter(bundle => {
      if (bundle.status === BundleStatus.Draft) {
        return false;
      }
      if (isBundlePickedUp(bundle)) {
        return true;
      }
      const dep = departureTime(bundle);
      if (readyListItems > 0 && dep && dep.diff(now, 'minute') < 60) {
        readyListItems--;
        return true;
      }
      return false;
    });
  }

  getPickedUpBundles(activeBundles: BundleJson[]): BundleJson[] {
    const now = dayjs();
    const bundleReadyTimes = this.state.bundleReadyTimes;

    return activeBundles.filter(bundle => {
      const bundleReadyTime = bundleReadyTimes[bundle.id];
      return (
        bundleReadyTime &&
        now.diff(bundleReadyTime, 'minute') > 2 &&
        isBundlePickedUp(bundle)
      );
    });
  }

  async load() {
    try {
      const bundles = await this.fetchBundles();
      const couriers = await this.fetchCouriers(bundles);

      this.sortBundles(bundles);

      const activeBundles = this.getReadyBundles(bundles);

      const pickedUpBundles = this.getPickedUpBundles(activeBundles);
      const pickedUpBundleIds = pickedUpBundles.map(b => b.id);

      const readyBundles = activeBundles.filter(
        b => !pickedUpBundleIds.includes(b.id)
      );
      const readyBundleIds = readyBundles.map(b => b.id);
      const nextBundles = bundles.filter(
        b => !readyBundleIds.includes(b.id) && !pickedUpBundleIds.includes(b.id)
      );

      const bundleReadyTimes = this.state.bundleReadyTimes;
      activeBundles.forEach(bundle => {
        if (!bundleReadyTimes[bundle.id] && isBundlePickedUp(bundle)) {
          bundleReadyTimes[bundle.id] = dayjs();
        }
      });

      this.setState({
        loading: false,
        readyBundles: readyBundles,
        nextBundles: nextBundles,
        bundleReadyTimes: bundleReadyTimes,
        couriers: couriers,
      });
    } catch (e) {
      console.warn('Could not reload bundles for Hub dashboard', e);
    }

    this.refreshTimerId = setTimeout(
      () => this.load(),
      this.getRefreshTimeSeconds() * 1000
    ) as any;
  }

  getRefreshTimeSeconds(): number {
    const hubPickupStartTime = dayjs()
      .hour(16)
      .minute(0)
      .second(0)
      .millisecond(0);
    const hubPickupEndTime = dayjs()
      .hour(17)
      .minute(30)
      .second(0)
      .millisecond(0);
    const now = dayjs();

    if (
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1'
    ) {
      return 15; // dev mode
    }
    if (now.isBefore(hubPickupStartTime) || now.isAfter(hubPickupEndTime)) {
      return 60 * 5; // every 5 minutes before/after hub time
    }
    return 15; // every 15 seconds, during hub time
  }

  async fetchCouriers(
    bundles: BundleJson[]
  ): Promise<{ [courierId: string]: CourierJson }> {
    const allCourierIds = bundles
      .map(bundle =>
        bundle.assignedCourierId ? Number(bundle.assignedCourierId) : null
      )
      .filter(b => !!b);
    const courierIds = unique(allCourierIds).sort();
    if (courierIds.length === 0) {
      return {};
    }
    const currentCourierIds = Object.keys(this.state.couriers).sort();
    if (courierIds.join() === currentCourierIds.join()) {
      return this.state.couriers;
    }
    return await fetchCourierByIdList(courierIds);
  }

  async fetchBundles(): Promise<BundleJson[]> {
    const bundleStatuses = [
      BundleStatus.Published,
      BundleStatus.Proposed,
      BundleStatus.Accepted,
      BundleStatus.Draft,
    ];
    const region = this.props.region;
    const response = await Fetch.get({
      url: `${settings.newApiUrl}/bundle?status=${bundleStatuses.join(
        ','
      )}&region=${region}`,
    });

    if (response.ok) {
      const bundleResponse: BundlesPageJson = await response.json();
      return plainToClass(BundleJson, bundleResponse.items);
    } else {
      return [];
    }
  }

  render() {
    const { loading, readyBundles, nextBundles, couriers, randomTileFlip } =
      this.state;
    const bundlesAvailable = readyBundles.length > 0 || nextBundles.length > 0;
    const placeHolders = readyBundles.length > 5 ? 0 : 5 - readyBundles.length;

    return (
      <>
        <Helmet>
          <title>Monitor - Hub</title>
        </Helmet>
        {loading && (
          <div className="hub-bundles--empty">
            <Loader type="bounce" size="xxl" />
          </div>
        )}
        {!loading && !bundlesAvailable && (
          <div className="hub-bundles--empty">
            <h2>Waiting for active bundles...</h2>
            <Loader type="tile" size="xxl" />
          </div>
        )}
        {!loading && bundlesAvailable && (
          <div className="hub-bundles">
            <div className="hub-bundles-left">
              <div className="hub-bundles-ready-title">
                <h3>Now Serving</h3>
                <h4>{this.state.time.format('HH:mm:ss')}</h4>
              </div>
              <div className="hub-bundles-ready-header">
                <span> &nbsp;</span>
                <span>
                  <em> Name</em>
                </span>
                <span>
                  <em> Bundle </em>
                </span>
                <span>
                  <em>Shelf</em>
                </span>
                <span>
                  <em>Dep. Time</em>
                </span>
              </div>
              <div className="hub-bundles-ready">
                {readyBundles.map((bundle, idx) => (
                  <BundleItem
                    key={bundle.id}
                    bundle={bundle}
                    courier={couriers[bundle.assignedCourierId]}
                    animate={randomTileFlip === idx}
                    icon={true}
                  />
                ))}
                {[...Array(placeHolders)].map((v, idx) => (
                  <div className="hub-bundle" key={`empty-${idx}`}>
                    &nbsp;
                  </div>
                ))}
              </div>
            </div>
            <div className="hub-bundles-right">
              <div className="hub-bundles-next">
                <div className="hub-bundles-next-title">
                  <h3>Up Next</h3>
                </div>

                <div className="hub-bundles-next-header">
                  <span>
                    <em> Name</em>
                  </span>
                  <span>
                    <em> Bundle </em>
                  </span>
                  <span>
                    <em>Shelf</em>
                  </span>
                  <span>
                    <em>Dep. Time</em>
                  </span>
                </div>
                <div className="hub-bundles-next">
                  {nextBundles.map((bundle, idx) => (
                    <BundleItem
                      key={bundle.id}
                      bundle={bundle}
                      courier={couriers[bundle.assignedCourierId]}
                      animate={false}
                      icon={false}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }
}

const roundDownTime = (dateToConvert: number, roundAmount: number) => {
  return Math.floor(dateToConvert / roundAmount) * roundAmount;
};

const departureTime = (bundle: BundleJson): Dayjs | null => {
  const firstEta = bundle.instructions.length > 0 && bundle.instructions[0].eta;

  if (firstEta && firstEta.isValid()) {
    const usedEta = new Date(firstEta.toISOString());

    const roundAmount = 1000 * 60 * 15; /* 15 minutes */
    const roundedEta = roundDownTime(Number(usedEta), roundAmount);

    return dayjs(new Date(roundedEta));
  }

  return null;
};

function BundleItem({
  bundle,
  courier,
  animate,
  icon,
}: {
  bundle: BundleJson;
  courier?: CourierJson;
  animate: boolean;
  icon: boolean;
}) {
  return (
    <div
      className={classNames(
        {
          'hub-bundle-pickedup': isBundlePickedUp(bundle),
          'hub-bundle__animate': animate,
        },
        'hub-bundle'
      )}
    >
      {icon && <CourierVehicle courier={courier} />}
      {courier && <h4>{bundle.assignedCourierName}</h4>}
      {!courier && (
        <h4 className="hub-bundle__nocourier">No courier assigned</h4>
      )}

      <span className="hub-bundle__bundleid">
        <a
          href={`/bundles/${bundle.id}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {bundle.id}
        </a>
      </span>
      <span className="hub-bundle__shelf">{bundle.shelfId}</span>
      <span className="hub-bundle__departure">
        {departureTime(bundle)?.format('HH:mm') || '…'}
      </span>
    </div>
  );
}

function CourierVehicle({ courier }: { courier: CourierJson }) {
  if (!courier) {
    return (
      <div className="hub-bundle__information-vehicle">
        <CarIcon style={{ visibility: 'hidden' }} />
      </div>
    );
  }
  if (courier.travelMode === TravelMode.BIKE) {
    return (
      <div className="hub-bundle__information-vehicle">
        <BikeIcon />
      </div>
    );
  } else {
    return (
      <div className="hub-bundle__information-vehicle">
        <CarIcon />
      </div>
    );
  }
}
